import * as React from "react";
import { Button, Text } from "@uikit";
import Layout from "@components/layout";
import { navigate, graphql } from "gatsby";
import CourseTitle from "@components/course/courseTitle";
import HeroImages from "@components/common/heroimages";
import InfoModal from "@components/course/infoModal";
import CourseReviews from "@components/course/reviews";
import LocationMapAddress from "@components/common/locationMapAddress";
import CourseInformation from "@components/course/courseInformation";
import CourseFacilities from "@components/course/courseFacilities";
import CourseAwards from "@components/course/courseAwards";
import CourseTees from "@components/course/courseTees";
import CourseTechnicalDifficulty from "@components/course/courseTechnicalDifficulty";
import CourseHandicapRequirements from "@components/course/courseHandicapRequirements";
import CourseFAQs from "@components/course/faqs";
import { useIsVisible } from "@hooks/useIsVisible";

import NearbyAttractions from "@components/common/nearbyAttractions";
import NearbyAreas from "@components/common/nearbyAreas";
import NearbyCourses from "@components/common/nearbyCourses";
import NearbyHotels from "@components/common/nearbyHotels";
import OtherCourses from "@components/course/otherCourses";
import CoursePrices from "@components/course/coursePrices";
import ReviewsModal from "@components/course/reviews/reviewModal";
import CoursePackages from "@components/course/coursePackages";
import {
  getGroupSizeBoundsForContracts,
  slugify,
  Course,
  CourseRate,
  Image,
  CourseClosure,
  getCoursePricing,
} from "@kalabo/golfsavers-common";

import { isRegionalSite, UpsertRecentlyViewed } from "@utilities/core";
import { useApp } from "@store/app";
import { useBook } from "@store/book";
import { useWindow } from "@hooks/useWindow";
import Seo from "@components/seo";
import CourseWeatherDisplay from "@components/course/courseWeather";
import RateSearch from "@components/course/rateSearch";
import { STORAGE_COURSE_KEY } from "@utilities/constants";
import dayjs from "dayjs";

interface IProps {
  data: any;
  pageContext: pageContextProp;
}

interface pageContextProp {
  id: string;
  slug: string;
  country_id: string;
  region_id: string;
}

const CourseTemplate = ({ data, pageContext }: IProps) => {
  const [showReviewModal, setShowReviewModal] = React.useState(false);
  const win = useWindow();

  const date = useApp(($) => $.date);
  const golfers = useApp(($) => $.golfers);

  const saveRateValues = useBook(($) => $.saveRateValues);
  const resetRate = useBook(($) => $.resetRate);
  const setWhereWhenWho = useBook(($) => $.setWhereWhenWho);
  const setTransferWhereWhenWho = useBook(($) => $.setTransferWhereWhenWho);

  const infoModalRef = React.useRef<any>();
  const imageRef = React.useRef<any>();

  const aboutJumpLink = React.useRef<any>();
  const reviewJumpLink = React.useRef<any>();
  const courseInfoJumpLink = React.useRef<any>();
  const pricingJumpLink = React.useRef<any>();
  const locationJumpLink = React.useRef<any>();
  const faqJumpLink = React.useRef<any>();
  const packagesJumpLink = React.useRef<any>();

  const isImageVisible = useIsVisible(imageRef);

  let current_region = data.regions.edges?.find(
    (region: any) =>
      region.node.region_id ===
      (process.env.GATSBY_REGION_ID || pageContext?.region_id)
  ).node;

  const course_closures = React.useMemo(() => {
    return data.course_closures.edges.map((edge: any) => {
      return edge.node;
    }) as CourseClosure[];
  }, [data]);

  const course = React.useMemo(() => {
    return data.allGolfsaversGolfCourses.edges[0].node as Course;
  }, [data]);

  const hole_layouts_array = React.useMemo(() => {
    return course?.hole_layouts
      ? (JSON.parse(course?.hole_layouts?.toString()) as Image[])
      : [];
  }, [course]);
  const course_layouts_array = React.useMemo(() => {
    return course?.course_layouts
      ? (JSON.parse(course?.course_layouts?.toString()) as Image[])
      : [];
  }, [course]);
  const scorecards_array = React.useMemo(() => {
    return course?.scorecards
      ? (JSON.parse(course?.scorecards?.toString()) as Image[])
      : [];
  }, [course]);

  React.useEffect(() => {
    UpsertRecentlyViewed(STORAGE_COURSE_KEY, course.course_id);
  }, [course]);

  React.useEffect(() => {
    if (window.golfsavers_dl && course) {
      console.log(course);

      let price_obj =
        getCoursePricing(
          dayjs().add(2, "day").format("YYYY-MM-DD"),
          course.golf_contracts
        ) || 0;

      window.golfsavers_dl.push({ ecommerce: null });
      window.golfsavers_dl.push({
        event: "view_item",
        ecommerce: {
          currency: price_obj?.currency,
          value: price_obj?.price,
          items: [
            {
              item_id: course.slug,
              item_name: course.name.en,
              item_brand: "Golfsavers",
              item_variant: "Course",
              item_list_id: `${course?.region?.slug}-golf-courses`,
              item_list_name: `${course?.region.name.en} Golf Courses`,
              price: Math.round(Number(price_obj.price) * 100) / 100,
              quantity: 1,
            },
          ],
        },
      });
    }
  }, [course]);

  const groupSizes = React.useMemo(() => {
    let latest_contract = getGroupSizeBoundsForContracts(
      date,
      course.golf_contracts
    );
    return latest_contract;
  }, [date, course]);

  const public_holidays = React.useMemo(() => {
    return data.allGolfsaversPublicHolidays.edges.map((edge: any) => {
      return edge.node;
    });
  }, [data]);

  const airports = data.airports.edges.map((a: any) => {
    return a.node;
  });

  const agent = data.agents.edges[0].node;

  let breadcrumb = React.useMemo(() => {
    if (isRegionalSite()) {
      return [
        { name: "Home", url: "/" },
        {
          name: `${current_region.name.en} Golf Courses`,
          url: `/${slugify(current_region.slug)}-golf-courses`,
        },
        { name: course.nickname.en, url: "" },
      ];
    } else {
      return [
        { name: "Home", url: "/" },
        {
          name: current_region.country.name.en,
          url: `/${slugify(current_region.country.slug)}`,
        },
        {
          name: `${current_region.name.en} Golf Courses`,
          url: `/${slugify(current_region.country.slug)}/${slugify(
            current_region.slug
          )}-golf-courses`,
        },
        { name: course.nickname.en, url: "" },
      ];
    }
  }, []);

  if (!course) return null;

  return (
    <>
      <Layout fullWidth breadcrumb={breadcrumb} className="mb-20">
        <div className="lg:max-w-[1000px] xl:max-w-[1120px] mx-auto px-[20px] md:px-10 lg:px-0 w-full">
          <div className="flex flex-col">
            <CourseTitle
              course_closures={course_closures}
              course={course}
              onShowReviews={() => {
                setShowReviewModal(true);
              }}
              onShowMap={() => {
                infoModalRef.current && infoModalRef.current.open(3);
              }}
            />
            <div className="mb-0 sm:mb-10">
              <HeroImages
                course={course}
                onClick={() => {
                  infoModalRef.current && infoModalRef.current.open(2);
                }}
                images={course.images}
                recommended={course.is_recommended}
              />
            </div>
            <div ref={imageRef}></div>
          </div>{" "}
          <div
            className={` sticky -mx-5 sm:mx-0 sm:fixed sm:left-0 sm:right-0 top-[-1px] z-[900] ${
              isImageVisible ? "sm:hidden" : "sm:block"
            }`}
          >
            <div className="bg-white border-b border-grey3 items-center mb-10 flex">
              <div className="lg:max-w-[1000px] xl:max-w-[1120px] mx-auto px-5 sm:px-10 lg:px-0 min-h-100 flex w-full overflow-auto noscrollbar">
                <div className="flex gap-[30px] sm:gap-10">
                  <div
                    className="border-b-[3px] border-transparent hover:border-black flex items-center h-[60px] cursor-pointer"
                    onClick={() => {
                      if (win) {
                        win.scrollTo({
                          top: aboutJumpLink.current.offsetTop - 100,
                          behavior: "smooth",
                        });
                      }
                    }}
                  >
                    <Text
                      size={15}
                      weight="semibold"
                      color={"black90"}
                      className="mt-[5px]"
                    >
                      About
                    </Text>
                  </div>
                  <div
                    className="border-b-[3px] border-transparent hover:border-black flex items-center h-[60px] cursor-pointer"
                    onClick={() => {
                      if (win) {
                        win.scrollTo({
                          top: reviewJumpLink.current.offsetTop - 58,
                          behavior: "smooth",
                        });
                      }
                    }}
                  >
                    <Text
                      size={15}
                      weight="semibold"
                      color={"black90"}
                      className="mt-[5px]"
                    >
                      Reviews
                    </Text>
                  </div>
                  <div
                    className="border-b-[3px] border-transparent hover:border-black flex items-center h-[60px] cursor-pointer"
                    onClick={() => {
                      if (win) {
                        win.scrollTo({
                          top: courseInfoJumpLink.current.offsetTop - 58,
                          behavior: "smooth",
                        });
                      }
                    }}
                  >
                    <Text
                      size={15}
                      weight="semibold"
                      color={"black90"}
                      className="whitespace-nowrap mt-[5px]"
                    >
                      Course Info
                    </Text>
                  </div>{" "}
                  {course.golf_contracts.length > 0 && (
                    <div
                      className="border-b-[3px] border-transparent hover:border-black flex items-center h-[60px] cursor-pointer"
                      onClick={() => {
                        if (win) {
                          win.scrollTo({
                            top: pricingJumpLink.current.offsetTop - 58,
                            behavior: "smooth",
                          });
                        }
                      }}
                    >
                      <Text
                        size={15}
                        weight="semibold"
                        color={"black90"}
                        className="mt-[5px]"
                      >
                        Prices
                      </Text>
                    </div>
                  )}
                  <div
                    className="border-b-[3px] border-transparent hover:border-black flex items-center h-[60px] cursor-pointer"
                    onClick={() => {
                      if (win) {
                        win.scrollTo({
                          top: locationJumpLink.current.offsetTop - 58,
                          behavior: "smooth",
                        });
                      }
                    }}
                  >
                    <Text
                      size={15}
                      weight="semibold"
                      color={"black90"}
                      className="mt-[5px]"
                    >
                      Location
                    </Text>
                  </div>
                  <div
                    className="border-b-[3px] border-transparent hover:border-black flex items-center h-[60px] cursor-pointer"
                    onClick={() => {
                      if (win) {
                        win.scrollTo({
                          top: faqJumpLink.current.offsetTop - 58,
                          behavior: "smooth",
                        });
                      }
                    }}
                  >
                    <Text
                      size={15}
                      weight="semibold"
                      color={"black90"}
                      className="mt-[5px]"
                    >
                      FAQ
                    </Text>
                  </div>
                  {course.hotels.length > 0 && (
                    <div
                      className="border-b-[3px] border-transparent hover:border-black flex items-center h-[60px] mr-16 cursor-pointer"
                      onClick={() => {
                        if (win) {
                          win.scrollTo({
                            top: packagesJumpLink.current.offsetTop - 100,
                            behavior: "smooth",
                          });
                        }
                      }}
                    >
                      <Text
                        size={15}
                        weight="semibold"
                        color={"black90"}
                        className="mt-[5px] whitespace-nowrap"
                      >
                        Golf Packages
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <a id="about" ref={aboutJumpLink} />
          <div className="grid grid-cols-12 lg:gap-x-[60px]">
            <div className="col-span-12 sm:col-span-12 lg:col-span-7 ">
              <Text
                size={21}
                color="black"
                weight="semibold"
                leading={1.3}
                element="h2"
                className="mb-[25px] lg:mb-[30px]"
              >
                About
              </Text>
              <Text
                size={17}
                color="black80"
                leading={1.5}
                weight="semibold"
                className="mb-[10px]"
                element="h3"
              >
                {course?.summary?.en}
              </Text>
              <Text
                size={15}
                leading={1.5}
                element="p"
                color="black80"
                dangerouslySetInnerHTML={{ __html: course?.short_desc?.en }}
              />
              {course?.description?.en && (
                <Text
                  lineClamp={0}
                  size={15}
                  leading={1.5}
                  element="p"
                  clampExpand={true}
                  color="black80"
                  dangerouslySetInnerHTML={{ __html: course?.description?.en }}
                />
              )}

              <div className="flex flex-wrap gap-x-2 gap-y-2 pt-4 mt-[10px] mb-10">
                {course.video_url && (
                  <Button
                    text="Video"
                    icon="play"
                    color="grey7"
                    invert
                    className="rounded-[40px] !px-[14px] hover:bg-grey !mb-0"
                    size="medium"
                    onClick={() => {
                      infoModalRef.current && infoModalRef.current.open(1);
                    }}
                  />
                )}
                <Button
                  text="View Map"
                  color="grey7"
                  invert
                  className="rounded-[40px] !px-[14px] hover:bg-grey  !mb-0"
                  size="medium"
                  onClick={() => {
                    infoModalRef.current && infoModalRef.current.open(3);
                  }}
                />
                {course_layouts_array && course_layouts_array.length > 0 && (
                  <Button
                    text="Course Layout"
                    color="grey7"
                    invert
                    className="rounded-[40px] !px-[14px] hover:bg-grey  !mb-0"
                    size="medium"
                    onClick={() => {
                      infoModalRef.current && infoModalRef.current.open(4);
                    }}
                  />
                )}
                {hole_layouts_array && hole_layouts_array.length > 0 && (
                  <Button
                    text="Hole by Hole"
                    color="grey7"
                    invert
                    className="rounded-[40px] !px-[14px] hover:bg-grey  !mb-0"
                    size="medium"
                    onClick={() => {
                      infoModalRef.current && infoModalRef.current.open(5);
                    }}
                  />
                )}
                {scorecards_array && scorecards_array.length > 0 && (
                  <Button
                    text="Scorecard"
                    color="grey7"
                    invert
                    className="rounded-[40px] !px-[14px] hover:bg-grey  !mb-0"
                    size="medium"
                    onClick={() => {
                      infoModalRef.current && infoModalRef.current.open(6);
                    }}
                  />
                )}
              </div>

              <a id="reviews" ref={reviewJumpLink} />
              <CourseReviews
                reviews={course.reviews?.reviews}
                onShowModal={() => {
                  setShowReviewModal(true);
                }}
              />
              <div className="flex flex-col gap-10">
                <div>
                  <a id="courseInfo" ref={courseInfoJumpLink} />
                  <CourseInformation
                    groupSizes={groupSizes}
                    architect={course.architect}
                    holes={course.holes}
                    days_of_operation={course.days_of_operation}
                    year_built={course.year_built}
                    grass_type={course.grass_type}
                    type={course.course_type}
                  />
                </div>
                <CourseAwards awards={course.awards} />
                <CourseFacilities facilities={course?.facilities} />
                <CourseTees data={course.tee_details} />
                <CourseTechnicalDifficulty
                  difficulty={course.technical_difficulty}
                />
                <CourseHandicapRequirements
                  course_name={course.name.en}
                  handicap={course?.policies?.handicap_requirements}
                />
                <div>
                  <a id="prices" ref={pricingJumpLink} />
                  <CoursePrices
                    rates={course.rates}
                    contracts={course.golf_contracts}
                    weekend_days={course.region.weekend_days}
                  />
                </div>
              </div>
              <a id="location" ref={locationJumpLink} />
              <div className=" border-t pt-10  border-grey3 ">
                <LocationMapAddress
                  onClick={() => {
                    infoModalRef.current && infoModalRef.current.open(3);
                  }}
                  title="Course Location"
                  address={course.full_address}
                  description={course?.location_description?.en}
                  longitude={course.longitude}
                  latitude={course.latitude}
                />
                <NearbyAreas
                  title="Nearby Airports"
                  types={["Airport"]}
                  maxDistance={100}
                  center={{
                    latitude: course.latitude,
                    longitude: course.longitude,
                  }}
                />

                <NearbyAttractions
                  center={{
                    latitude: course.latitude,
                    longitude: course.longitude,
                  }}
                  maxDistance={100}
                />
                <div className="flex flex-col sm:flex-row sm:gap-10">
                  <NearbyCourses
                    courses={course.nearby_courses}
                    region={current_region}
                    center={{
                      latitude: course.latitude,
                      longitude: course.longitude,
                    }}
                    maxDistance={100}
                  />
                  <NearbyHotels
                    region={current_region}
                    center={{
                      latitude: course.latitude,
                      longitude: course.longitude,
                    }}
                    maxDistance={200}
                  />
                </div>
              </div>
              <CourseWeatherDisplay
                data={course.course_weather}
                location={course.name.en}
              />
              <a id="faq" ref={faqJumpLink} />
              <CourseFAQs course={course} />
            </div>
            <div className="sm:col-span-5 lg:col-span-5">
              {win && (
                <>
                  <RateSearch
                    agent={agent}
                    contracts={course.golf_contracts}
                    course_id={course.course_id}
                    private_club={course.private_club}
                    public_holidays={public_holidays}
                    weekend_days={course.region.weekend_days}
                    onSelected={(rate: CourseRate) => {
                      // if prepayment of caddie is not avail need to preset this value.

                      if (window.golfsavers_dl) {
                        window.golfsavers_dl.push({ ecommerce: null });
                        window.golfsavers_dl.push({
                          event: "select_item",
                          ecommerce: {
                            item_list_id: `${course?.region.slug}-golf-courses`,
                            item_list_name: `${course?.region.name.en} Golf Courses`,
                            items: [
                              {
                                item_id: course.slug,
                                item_name: course.name.en,
                                item_brand: "Golfsavers",
                                item_variant: rate?.product_sku,
                                item_category: rate?.product_name?.en,
                                item_list_id: `${current_region.slug}-golf-courses`,
                                item_list_name: `${current_region.name.en} Golf Courses`,
                                price: rate?.sell_price,
                                quantity: 1,
                              },
                            ],
                          },
                        });
                      }

                      resetRate();

                      setWhereWhenWho({
                        number_of_golfers: golfers,
                        date: date,
                        region_id: course.region.id,
                        country_id: course.region?.country?.id,
                      });

                      setTransferWhereWhenWho({
                        number_of_passengers: golfers,
                        date: date,
                        region_id: course.region.id,
                        country_id: course.region?.country?.id,
                      });

                      saveRateValues({ rate, course });
                      navigate(`/book/${rate.rate_id}?course=${course.slug}`);
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </div>

        {course.hotels.length > 0 && (
          <div className="bg-grey py-[60px]">
            <a id="packages" ref={packagesJumpLink} />
            <div className="lg:max-w-[1000px] xl:max-w-[1120px] mx-auto px-0  lg:px-0 w-full">
              <CoursePackages region={current_region} course={course} />
            </div>
          </div>
        )}
        <div className="lg:max-w-[1000px] xl:max-w-[1120px] mx-auto px-[20px] md:px-10 lg:px-0 w-full">
          <OtherCourses
            courses={course.nearby_courses}
            region={current_region}
            center={{
              latitude: course.latitude,
              longitude: course.longitude,
            }}
            maxDistance={100}
          />
        </div>
        <InfoModal
          ref={infoModalRef}
          scorecards={scorecards_array}
          course_layouts={course_layouts_array}
          airports={airports}
          hole_layouts={hole_layouts_array}
          video_url={course.video_url}
          longitude={course.longitude}
          latitude={course.latitude}
          images={course.images}
        />
      </Layout>

      <ReviewsModal
        onClose={() => {
          setShowReviewModal(false);
        }}
        courseName={course.name.en}
        course_id={course.course_id}
        visible={showReviewModal}
      />
    </>
  );
};
export default CourseTemplate;

export const Head = ({ data }: IProps) => {
  const course = React.useMemo(() => {
    return data.allGolfsaversGolfCourses.edges[0].node as Course;
  }, [data]);

  return (
    <Seo
      title={`${course.name.en} | Discount Tee Times`}
      description={`Book discounted tee times at ${course.name.en} in ${course.region.name.en}. Book with confidence. Best price guarantee. Spend less with ${process.env.GATSBY_SITE_URL}.`}
      keywords={``}
      canonical={``}
    />
  );
};

export const query = graphql`
  query ($id: String, $country_id: String!, $region_id: String!) {
    airports: allGolfsaversAreas(
      filter: { type: { eq: "Airport" }, region_id: { eq: $region_id } }
    ) {
      edges {
        node {
          latitude
          longitude
          name {
            en
          }
          type
        }
      }
    }
    course_closures: allGolfsaversCourseClosure(
      filter: { course_id: { eq: $id } }
    ) {
      edges {
        node {
          id
          start_time
          start_date
          days {
            fri
            hol
            mon
            sat
            sun
            thu
            tue
            wed
            ph
            pub
          }
          end_time
          end_date
          reason
          course_id
        }
      }
    }
    agents: allGolfsaversAgents {
      edges {
        node {
          config {
            email {
              contact_email
              contact_phone
              reply_address
              from_alias
            }
          }
        }
      }
    }
    regions: allGolfsaversRegions(filter: { country_id: { eq: $country_id } }) {
      edges {
        node {
          id
          top_courses
          region_id
          name {
            en
          }
          slug
          country {
            id
            name {
              en
            }
            slug
          }
          course_listing_image
          package_listing_image
        }
      }
    }
    allGolfsaversPublicHolidays(filter: { country_id: { eq: $country_id } }) {
      edges {
        node {
          area_id
          country_id
          end_date
          id
          name
          regions
          start_date
        }
      }
    }
    allGolfsaversCountries(filter: { country_id: { eq: $country_id } }) {
      nodes {
        id
        country_id
        name {
          en
        }
      }
    }
    allGolfsaversGolfCourses(filter: { course_id: { eq: $id } }) {
      edges {
        node {
          id
          course_id
          private_club
          course_weather {
            weather_data {
              daily {
                summary
                temp {
                  max
                  min
                }
                dt
                weather {
                  main
                  id
                  icon
                  description
                }
                humidity
                wind_speed
                wind_deg
              }
              timezone
            }
            created_at
          }
          nearby_courses {
            id
            course_id
            slug
            region_id
            longitude
            latitude
            featured_image {
              alt
              url
            }
            days_of_operation {
              fri
              hol
              mon
              sat
              sun
              thu
              tue
              wed
            }
            name {
              en
            }
            reviews {
              count
              average_rating
              reviews {
                id
                overall
              }
            }
            summary {
              en
            }
            price_range
            location_description {
              en
            }
            golf_contracts {
              type
              start_date
              id
              products {
                caddie_price
                caddie_price_enabled
                contract_id
                id
                note
                walk_in_rate
                single_cart_price
                single_cart_enabled
                shared_cart_price
                shared_cart_enabled
                product_id
                odd_number_surcharge_enabled
                product {
                  config {
                    transfers
                    single_cart
                    shared_cart
                    shared_caddie
                    meals
                    caddie
                    holes
                    all_inclusive
                  }
                  type
                  name {
                    en
                  }
                }
                rates {
                  availability_days {
                    fri
                    hol
                    mon
                    sat
                    sun
                    thu
                    tue
                    wed
                  }
                  sell_price
                  reference_rate
                  product_type_id
                  multiples
                  min_pax
                  max_pax
                  margin
                  latest_time
                  id
                  earliest_time
                  cost_price
                  contract_product_id
                  cart_policy
                  caddie_policy
                }
              }
              end_date
              currency_code
              created_at
              course_id
              booking_window_start_date
              booking_window_end_date
            }
          }
          hotels {
            longitude
            latitude
            id
            name {
              en
            }
            area {
              id
              name {
                en
              }
            }
            packages {
              name
              course_combo
              currency_code
              group_size
              number_of_nights
              slug
              room {
                board_type
                room_type
                room_basis
                prices {
                  cost
                  from_date
                  margin
                  to_date
                  total
                }
              }
              courses {
                id
                prices {
                  green_fees {
                    from_date
                    sell_price
                    to_date
                  }
                  transfer_price
                }
                slug
              }
            }
            images {
              alt
              url
            }
            slug
            star_rating
          }
          slug
          full_address
          architect
          region_id
          male_handicap
          female_handicap
          longitude
          latitude
          holes
          caddie_must_pay_at_course
          caddies
          cart_must_pay_at_course
          club_rental
          shoe_rental
          technical_difficulty
          restaurant
          created_at
          location_description {
            en
          }
          rates {
            extras {
              prices {
                currency
                notes
                price
                type
              }
              enabled
            }
            equipment {
              prices {
                currency
                notes
                price
                to_price
                type
              }
              enabled
            }
          }
          facilities {
            caddies
            club_rental
            driving_range
            golf_carts
            golf_lessons
            golf_shop
            locker_room
            night_golf
            onsite_accommodation
            practice_green
            shoe_rental
            spa_and_massage
            restaurant
          }
          golf_carts
          golf_lessons
          golf_shop
          awards {
            awarded_by
            name
            year
          }
          policies {
            handicap_requirements {
              certificate_required
              male_handicap
              female_handicap
              enabled
            }
            carts {
              enabled
              odd_no_surcharge
              policy
              prepayment_available
              shared_carts
              single_carts
            }
            caddies {
              enabled
              note
              policy
              prepayment_available
              type
            }
            advance_booking {
              enabled
              weekday
              weekend_ph
            }
            raincheck {
              config {
                en
              }
              enabled
            }
            non_playing_followers {
              enabled
              note
            }
            nationality_restriction {
              enabled
            }
            identity_requirement {
              enabled
              golfname_required
              passport_required
              nationality_required
            }
            single_golfers {
              enabled
            }
          }
          images {
            alt
            url
          }
          is_recommended
          night_golf
          onsite_accommodation
          practice_facilities
          spa_and_massage
          video_url
          short_desc {
            en
          }
          seo {
            canonical
            description
            title
            keywords
          }
          scorecards
          offers {
            currency
            lowPrice
            offerCount
            highPrice
          }
          name {
            en
          }
          nickname {
            en
          }
          summary {
            en
          }
          description {
            en
          }
          course_layouts
          reviews {
            count
            average_rating
            reviews {
              id
              name
              facilities
              created_at
              course_id
              condition
              approved
              overall
              pace
              review
              service
              show_name
              title
            }
          }
          hole_layouts
          grass_type {
            grass_location
            grass_type
          }
          year_built
          full_address
          course_type
          tee_details {
            note
            tees {
              ladies
              length
              tee
              slope_index
              rating
              par
            }
          }
          price_range
          location_description {
            en
          }
          days_of_operation {
            fri
            hol
            mon
            sat
            sun
            thu
            tue
            wed
          }
          region {
            id
            slug
            name {
              en
            }
            weekend_days {
              wed
              tue
              thu
              sun
              sat
              mon
              fri
            }
            points_of_reference {
              name
              longitude
              latitude
              distance
              create_filter
              create_faq
            }
            country {
              id
              name {
                en
              }
              slug
            }
          }
          golf_contracts {
            type
            supplier_id
            start_date
            prepaid_inventory
            platforms
            payment_terms
            modified_at
            id
            products {
              caddie_price
              caddie_price_enabled
              contract_id
              created_at
              id
              modified_at
              note
              walk_in_rate
              single_cart_price
              single_cart_enabled
              shared_cart_price
              shared_cart_enabled
              product_id
              odd_number_surcharge_enabled
              product {
                config {
                  transfers
                  single_cart
                  shared_cart
                  shared_caddie
                  meals
                  caddie
                  holes
                  all_inclusive
                }
                type
                sku
                name {
                  en
                }
                modified_by
                modified_at
                id
                created_by
                created_at
              }
              rates {
                availability_days {
                  fri
                  hol
                  mon
                  sat
                  sun
                  thu
                  tue
                  wed
                }
                sell_price
                reference_rate
                product_type_id
                multiples
                modified_at
                min_pax
                max_pax
                margin
                latest_time
                id
                earliest_time
                created_at
                cost_price
                contract_product_id
                cart_policy
                caddie_policy
              }
            }
            end_date
            currency_code
            created_at
            course_id
            booking_window_start_date
            booking_window_end_date
          }
        }
      }
    }
  }
`;
